/**
 * Feature Flag Utility
 * 
 * Provides functionality to check if features are enabled based on environment variables
 * and user store IDs.
 */

// Store IDs that are restricted to only sales rank monitoring related features
const SALES_RANK_ONLY_STORE_IDS = [];

// Pages that sales rank only stores are allowed to access
const SALES_RANK_ONLY_ALLOWED_PAGES = [
  '/sales-rank-monitoring',
  '/market-place',
  '/on-boarding'
];

/**
 * Check if a store is in "sales rank only" restricted mode
 * 
 * @param {string|number} storeId - The store ID to check
 * @returns {boolean} - Whether the store is restricted to sales rank only features
 */
export const isSalesRankOnlyStore = (storeId) => {
  return SALES_RANK_ONLY_STORE_IDS.includes(Number(storeId));
};

/**
 * Check if a store has access to a specific page
 * 
 * @param {string|number} storeId - The store ID to check
 * @param {string} pagePath - The page path to check access for
 * @returns {boolean} - Whether the store can access this page
 */
export const hasPageAccess = (storeId, pagePath) => {
  // If it's a sales rank only store, only allow specific pages
  if (isSalesRankOnlyStore(storeId)) {
    return SALES_RANK_ONLY_ALLOWED_PAGES.includes(pagePath);
  }
  
  // For non-restricted stores, check individual feature flags
  if (pagePath === '/ai-listing-generator') {
    return isAIListingGeneratorEnabled(storeId);
  }
  
  // All other pages accessible by default for non-restricted stores
  return true;
};

/**
 * Get the default redirect page for restricted stores
 * 
 * @param {string|number} storeId - The store ID to check
 * @returns {string} - The default page path for restricted stores
 */
export const getDefaultPageForRestrictedStore = (storeId) => {
  if (isSalesRankOnlyStore(storeId)) {
    return '/sales-rank-monitoring';
  }
  // Default page for unrestricted stores (avoid redirecting to '/' which is the login page)
  return '/dashboard';
};

/**
 * Get all accessible pages for a store (for navigation purposes)
 * 
 * @param {string|number} storeId - The store ID to check
 * @returns {array} - Array of accessible page paths
 */
export const getAccessiblePages = (storeId) => {
  if (isSalesRankOnlyStore(storeId)) {
    return SALES_RANK_ONLY_ALLOWED_PAGES;
  }
  
  // For unrestricted stores, return all pages except those with specific restrictions
  const allPages = [
    '/',
    '/sales-rank-monitoring',
    '/market-place',
    '/on-boarding',
    '/url-listing-analyzer',
    // Add other standard pages here
  ];
  
  // Add AI Listing Generator only if enabled
  if (isAIListingGeneratorEnabled(storeId)) {
    allPages.push('/ai-listing-generator');
  }
  
  return allPages;
};

/**
 * Check if a feature is enabled for a specific store ID
 * 
 * @param {string} featureName - The feature name (e.g., 'AI_GENERATOR')
 * @param {string|number} storeId - The store ID to check access for
 * @returns {boolean} - Whether the feature is enabled for this store
 */
export const isFeatureEnabled = (featureName, storeId) => {
  // Sales rank only stores cannot access AI_GENERATOR or its sub-features
  if (isSalesRankOnlyStore(storeId) && (featureName === 'AI_GENERATOR' || featureName.startsWith('AI_GENERATOR_'))) {
    return false;
  }
  
  // 1) Environment variable override (takes precedence if defined)
  // Note: Next.js only replaces direct property access like process.env.NEXT_PUBLIC_*
  // Dynamic access (process.env[envKey]) will NOT work in the browser bundle.
  const FEATURE_ENV_FLAGS = {
    AI_GENERATOR: process.env.NEXT_PUBLIC_FEATURE_FLAG_AI_GENERATOR,
    AI_GENERATOR_TEMPLATE_MODE: process.env.NEXT_PUBLIC_FEATURE_FLAG_AI_GENERATOR_TEMPLATE_MODE,
  };
  const flagValue = FEATURE_ENV_FLAGS[featureName];

  if (typeof flagValue === 'string') {
    if (flagValue === 'NONE') {
      return false;
    }
    if (flagValue === 'ALL') {
      return true;
    }
    // CSV of store IDs
    if (!storeId) {
      return false;
    }
    const allowedStoreIdsFromEnv = flagValue.split(',').map(id => id.trim());
    return allowedStoreIdsFromEnv.includes(String(storeId));
  }

  // 2) Fallback to hardcoded allowlists when no env flag is provided
  if (featureName === 'AI_GENERATOR') {
    const allowedStoreIds = [13, 48, 101, 102, 103, 104, 105, 106]; // 13 dev; prod: 48 Green Natural, 101 (Omnimedica), 103-106 Homburg
    return allowedStoreIds.includes(Number(storeId));
  }

  if (featureName === 'AI_GENERATOR_TEMPLATE_MODE') {
    // Template Mode is only available to a subset of AI_GENERATOR stores
    const allowedStoreIds = [13, 101, 48]; // 13 dev; prod: 48 Green Natural, 101 (Omnimedica)
    return allowedStoreIds.includes(Number(storeId));
  }

  // 3) Default deny for unknown features without env flags
  return false;
};

/**
 * Check if AI Listing Generator feature is enabled for a store
 * 
 * @param {string|number} storeId - The store ID to check
 * @returns {boolean} - Whether AI Listing Generator is enabled
 */
export const isAIListingGeneratorEnabled = (storeId) => {
  return isFeatureEnabled('AI_GENERATOR', storeId);
};

/**
 * Check if Template Mode within AI Listing Generator is enabled for a store
 * This is a sub-feature that requires AI_GENERATOR to be enabled first
 * 
 * @param {string|number} storeId - The store ID to check
 * @returns {boolean} - Whether Template Mode is available in AI Listing Generator
 */
export const isTemplateGenerationModeEnabled = (storeId) => {
  // First check if AI Listing Generator is enabled at all
  if (!isAIListingGeneratorEnabled(storeId)) {
    return false;
  }
  
  // Then check the template mode sub-feature
  return isFeatureEnabled('AI_GENERATOR_TEMPLATE_MODE', storeId);
};

/**
 * Get all enabled features for a store (for debugging/admin purposes)
 * 
 * @param {string|number} storeId - The store ID to check
 * @returns {object} - Object with feature names as keys and enabled status as values
 */
export const getEnabledFeatures = (storeId) => {
  const features = {};
  
  // Add feature flags
  features.AI_GENERATOR = isAIListingGeneratorEnabled(storeId);
  features.AI_GENERATOR_TEMPLATE_MODE = isTemplateGenerationModeEnabled(storeId);
  features.SALES_RANK_ONLY = isSalesRankOnlyStore(storeId);
  features.ACCESSIBLE_PAGES = getAccessiblePages(storeId);
  
  return features;
};
